import React from 'react';

import {
  TpHoverInfo,
  TpHoverRenderContext,
  TpHoverRenderInfo,
  ZoomPanCanvas,
} from '../../zoompan-canvas/ZoomPanCanvas';
import { TpZoomPanelInvPointMapInfo } from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpChartProps } from '../interfaces';
import { createDefinition } from './scatterPlot.Renderer';
import { ChartFilterButton, convertColumnDataToDisplayString } from '../../helpers/misc';
import { TpLassoSelection, getPointsInsideLassoSelection } from '../../zoompan-canvas/helpers';
import { TpEnSelectionFilterTypes } from '../../../../data/selection/interfaces';

export function ScatterPlot({ chartTypeManifest, dataContext, dataBinding, config, widgetContext }: TpChartProps) {
  const widgetGlobalConfig = widgetContext.dashboardContext.widgetGlobalConfig ?? {};
  const {
    definition,
    areaPoints,
    dataTable,
    dataX,
    dataY,
    dataItemId,
    dataHoverLabel,
    pointCount,
    nameX,
    nameY,
    colInfoX,
    colInfoY,
    colInfoHover,
    getItemIndexById,
    filterInfo,
    colorSchema,
    title,
  } = createDefinition(chartTypeManifest, dataContext, dataBinding, config, widgetGlobalConfig);

  if (definition.zoomPanXYGroup) {
    // we want the zoom & pan sync group to be unique per dashboard instance, so we offset it with a unique identifier for that instance
    definition.zoomPanXYGroup += widgetContext.dashboardContext.dashboardInstanceId;
  }

  function getHoverInfo(
    mappingInfo: TpZoomPanelInvPointMapInfo,
    logicPositX: number,
    logicPositY: number,
    renderPositX: number,
    renderPositY: number,
  ): TpHoverInfo | null {
    if (mappingInfo.area?.id != 'points') return null;
    let minDist = mappingInfo.mapsize(10);
    let selItemId = null;
    for (let pointNr = 0; pointNr < pointCount; pointNr++) {
      const scr_x = mappingInfo.mapX.map(dataX[pointNr]);
      const scr_y = mappingInfo.mapY.map(dataY[pointNr]);
      const dst = Math.abs(scr_x - renderPositX) + Math.abs(scr_y - renderPositY);
      if (dst <= minDist) {
        selItemId = dataItemId[pointNr];
        minDist = dst;
      }
    }
    if (selItemId == null) return null;
    return {
      dataTableId: dataBinding.dataTableId,
      itemId: selItemId,
    };
  }

  function getHoverRenderInfo(hoverInfo: TpHoverInfo, context: TpHoverRenderContext): TpHoverRenderInfo | null {
    if (hoverInfo.dataTableId != dataBinding.dataTableId || hoverInfo.catColumnId) return null;
    if (hoverInfo.itemId == null) return null;
    const hoverItemNr = getItemIndexById(hoverInfo.itemId);
    if (hoverItemNr === undefined) return null;
    const labels: string[] = [];
    if (context.isActiveChart) {
      if (dataHoverLabel) labels.push(convertColumnDataToDisplayString(colInfoHover, dataHoverLabel[hoverItemNr]));
      labels.push(`${nameX}=${convertColumnDataToDisplayString(colInfoX, dataX[hoverItemNr])}`);
      labels.push(`${nameY}=${convertColumnDataToDisplayString(colInfoY, dataY[hoverItemNr])}`);
    }
    return {
      area: areaPoints,
      logicPosX: dataX[hoverItemNr],
      logicPosY: dataY[hoverItemNr],
      labels,
    };
  }

  function onLassoSelection(lassoSelection: TpLassoSelection) {
    const points: { x: number; y: number; id: any }[] = [];
    for (let pointNr = 0; pointNr < pointCount; pointNr++) {
      const x = dataX[pointNr];
      const y = dataY[pointNr];
      if (!isNaN(x) && !isNaN(y)) points.push({ x, y, id: dataItemId[pointNr] });
    }
    const selectedPoints = getPointsInsideLassoSelection(lassoSelection, points);
    widgetContext.handleUserSelectionAction({
      type: TpEnSelectionFilterTypes.LASSO2D,
      dataTableId: dataTable.id,
      colIdX: colInfoX.id,
      colIdY: colInfoY.id,
      lassoPoints: lassoSelection.logicPoints,
      selectedItemIds: selectedPoints.map((pt) => pt.id),
    });
  }

  function onItemClicked(itemId: string) {
    widgetContext.handleOpenItem(dataTable.canonicalTypeId, itemId);
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ZoomPanCanvas
        definition={definition}
        getHoverInfo={getHoverInfo}
        getHoverRenderInfo={getHoverRenderInfo}
        onLassoSelection={onLassoSelection}
        onItemClicked={onItemClicked}
        maxZoomXRelative={30}
        maxZoomYRelative={30}
        widgetContext={widgetContext}
        colorSchema={colorSchema}
        chartTitleInfo={{
          assetId: dataContext.assetId,
          dataType: chartTypeManifest.id,
          title,
        }}
      />
      {filterInfo && <ChartFilterButton />}
    </div>
  );
}
